import React, { useEffect, useState, useCallback } from "react";
import { api, arabicError, fileToBase64 } from "../lib/api";
import { toast } from "sonner";
import { Plus, X, ChevronLeft, ChevronRight, Send, Trash2, Eye, Crop } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useFeatureEnabled } from "../context/FeatureContext";
import ImageCropperModal from "./ImageCropperModal";
import { logger } from "../lib/logger";

const CreateStoryModal = ({ onClose, onCreated }) => {
  const [rawImage, setRawImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [showCrop, setShowCrop] = useState(false);
  const [mode, setMode] = useState("crop"); // "crop" (9:16) or "original"
  const [caption, setCaption] = useState("");
  const [saving, setSaving] = useState(false);

  const onFile = async (f) => {
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("حجم الصورة أكبر من 10MB"); return; }
    const raw = await fileToBase64(f);
    setRawImage(raw);
    if (mode === "crop") {
      setShowCrop(true);
    } else {
      setPreview(raw);
    }
  };

  const changeMode = (next) => {
    setMode(next);
    if (!rawImage) return;
    if (next === "original") {
      setPreview(rawImage);
      setShowCrop(false);
    } else {
      setPreview(null);
      setShowCrop(true);
    }
  };

  const publish = async () => {
    if (!preview) { toast.error("اختر صورة أولاً"); return; }
    setSaving(true);
    try {
      await api.post("/stories", { image_base64: preview, caption });
      toast.success("تم نشر الستوري");
      onCreated();
      onClose();
    } catch (e) { toast.error(arabicError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[105] bg-black/60 flex items-center justify-center p-4" dir="rtl">
      <div className="bg-white rounded-2xl w-full max-w-md" data-testid="create-story-modal">
        <div className="p-4 border-b border-[#EBF4F6] flex items-center justify-between">
          <h3 className="font-extrabold text-[#333333]">ستوري جديد</h3>
          <button onClick={onClose} className="w-10 h-10 rounded-lg hover:bg-[#EBF4F6] flex items-center justify-center"><X /></button>
        </div>
        <div className="p-4 space-y-3">
          {/* Mode toggle */}
          <div className="grid grid-cols-2 gap-2 bg-[#EBF4F6] p-1 rounded-lg">
            <button
              type="button"
              onClick={() => changeMode("crop")}
              data-testid="story-mode-crop"
              className={`h-9 rounded-md font-bold text-sm flex items-center justify-center gap-1 ${mode === "crop" ? "bg-[#09637E] text-white" : "text-[#333333]"}`}
            >
              <Crop size={14} /> قصّ وتكبير (9:16)
            </button>
            <button
              type="button"
              onClick={() => changeMode("original")}
              data-testid="story-mode-original"
              className={`h-9 rounded-md font-bold text-sm ${mode === "original" ? "bg-[#09637E] text-white" : "text-[#333333]"}`}
            >
              الحجم الأصلي
            </button>
          </div>

          <label className="block cursor-pointer">
            <div className="border-2 border-dashed border-[#7AB2B2] rounded-xl p-6 text-center hover:bg-[#EBF4F6]/40">
              {preview ? (
                <div className="flex items-center justify-center gap-4">
                  <img src={preview} alt="" className="max-w-[140px] max-h-[180px] rounded-lg object-contain bg-black/5" />
                  {mode === "crop" ? (
                    <button type="button" onClick={(e) => { e.preventDefault(); setShowCrop(true); }} className="h-9 px-3 rounded-lg border-2 border-[#088395] text-[#088395] text-xs font-bold flex items-center gap-1">
                      <Crop size={14} /> إعادة القصّ
                    </button>
                  ) : (
                    <span className="text-xs text-[#666666] font-bold">النسبة الأصلية</span>
                  )}
                </div>
              ) : (
                <div className="text-[#09637E] font-bold">اضغط لاختيار صورة</div>
              )}
            </div>
            <input type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid="story-file-input" />
          </label>
          <textarea rows={2} value={caption} onChange={(e) => setCaption(e.target.value)} maxLength={200}
            placeholder="اكتب تعليقاً (اختياري)..." data-testid="story-caption-input"
            className="w-full rounded-lg border border-[#7AB2B2] px-3 py-2 focus:ring-2 focus:ring-[#09637E]" />
          <button onClick={publish} disabled={!preview || saving} data-testid="publish-story"
            className="w-full h-12 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-extrabold disabled:opacity-50">
            {saving ? "..." : "نشر"}
          </button>
        </div>
      </div>
      {showCrop && rawImage && mode === "crop" && (
        <ImageCropperModal
          imageSrc={rawImage}
          aspect={9 / 16}
          onCancel={() => setShowCrop(false)}
          onConfirm={(url) => { setPreview(url); setShowCrop(false); }}
        />
      )}
    </div>
  );
};

const StoryViewer = ({ group, groups, startIndex = 0, onClose, onDelete, onReloadGroups }) => {
  const { user } = useAuth();
  const [groupIdx, setGroupIdx] = useState(groups.findIndex((g) => g.merchant_id === group.merchant_id));
  const [itemIdx, setItemIdx] = useState(startIndex);
  const [reply, setReply] = useState("");
  const cur = groups[groupIdx];
  const story = cur?.stories[itemIdx];

  const markSeen = useCallback(async () => {
    if (!story || story.viewed) return;
    try { await api.post(`/stories/${story.id}/view`); } catch (err) { logger.warn("story view mark failed:", err); }
  }, [story]);

  useEffect(() => { markSeen(); }, [markSeen]);

  const next = () => {
    if (itemIdx + 1 < cur.stories.length) setItemIdx(itemIdx + 1);
    else if (groupIdx + 1 < groups.length) { setGroupIdx(groupIdx + 1); setItemIdx(0); }
    else onClose();
  };
  const prev = () => {
    if (itemIdx > 0) setItemIdx(itemIdx - 1);
    else if (groupIdx > 0) { const g = groups[groupIdx - 1]; setGroupIdx(groupIdx - 1); setItemIdx(g.stories.length - 1); }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    try { await api.post(`/stories/${story.id}/reply`, { message: reply.trim() }); toast.success("تم إرسال الرد"); setReply(""); }
    catch (e) { toast.error(arabicError(e)); }
  };

  const del = async () => {
    if (!window.confirm("حذف الستوري؟")) return;
    try { await api.delete(`/stories/${story.id}`); toast.success("تم الحذف"); onDelete && onDelete(); onReloadGroups && onReloadGroups(); onClose(); }
    catch (e) { toast.error(arabicError(e)); }
  };

  if (!story) return null;
  const isOwner = story.merchant_id === user?.id;

  return (
    <div className="fixed inset-0 z-[110] bg-black flex items-center justify-center" dir="rtl" data-testid="story-viewer">
      <button onClick={onClose} className="absolute top-4 left-4 w-11 h-11 rounded-full bg-white/90 flex items-center justify-center z-10"><X /></button>
      <button onClick={prev} className="absolute inset-y-0 right-0 w-1/4 flex items-center justify-start px-4 opacity-0 hover:opacity-100 text-white z-10"><ChevronRight /></button>
      <button onClick={next} className="absolute inset-y-0 left-0 w-1/4 flex items-center justify-end px-4 opacity-0 hover:opacity-100 text-white z-10"><ChevronLeft /></button>
      <div className="max-h-full max-w-md w-full relative">
        <div className="absolute top-4 inset-x-4 flex gap-1 z-10">
          {cur.stories.map((s, i) => (
            <div key={s.id || `bar-${i}`} className={`h-1 flex-1 rounded ${i < itemIdx ? "bg-white" : i === itemIdx ? "bg-white" : "bg-white/30"}`} />
          ))}
        </div>
        <div className="absolute top-8 inset-x-4 text-white font-bold flex items-center justify-between z-10">
          <div className="text-shadow">{cur.merchant_name}</div>
          {isOwner && (
            <button onClick={del} className="text-red-400 hover:text-red-300 flex items-center gap-1 text-xs" data-testid="delete-story">
              <Trash2 size={14} /> حذف
            </button>
          )}
        </div>
        <img src={story.image_url} alt="" className="max-h-[85vh] w-full object-contain" />
        {story.caption && (
          <div className="absolute bottom-20 inset-x-4 text-white text-center bg-black/50 rounded-lg py-2 px-3 text-sm">{story.caption}</div>
        )}
        {!isOwner && (
          <div className="absolute bottom-4 inset-x-4 flex gap-2 z-10">
            <input type="text" value={reply} onChange={(e) => setReply(e.target.value)}
              placeholder="اردّ على الستوري..." data-testid="story-reply-input"
              className="flex-1 h-11 rounded-full bg-white/90 px-4 focus:outline-none" />
            <button onClick={sendReply} className="w-11 h-11 rounded-full bg-[#09637E] text-white flex items-center justify-center" data-testid="story-reply-send">
              <Send size={18} />
            </button>
          </div>
        )}
        {isOwner && (
          <div className="absolute bottom-4 inset-x-4 text-center text-white/80 text-xs flex items-center justify-center gap-1">
            <Eye size={14} /> شاهدها {story.views?.length || 0} تاجراً
          </div>
        )}
      </div>
    </div>
  );
};

const StoriesRing = () => {
  const { user } = useAuth();
  const canPublish = useFeatureEnabled("stories");
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [viewer, setViewer] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/stories"); setGroups(r.data || []); }
    catch (err) { logger.warn("stories fetch failed:", err); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (user?.role !== "merchant") return null;

  return (
    <div className="mb-6" data-testid="stories-ring">
      <div className="flex gap-3 overflow-x-auto pb-2">
        {/* Add-my-story */}
        {canPublish && <button onClick={() => setShowCreate(true)} data-testid="add-story-btn" className="flex flex-col items-center min-w-[72px] gap-1">
          <div className="w-16 h-16 rounded-full bg-[#EBF4F6] border-2 border-dashed border-[#09637E] flex items-center justify-center text-[#09637E]">
            <Plus size={24} />
          </div>
          <span className="text-xs font-bold text-[#333333] truncate max-w-[72px]">ستوري جديد</span>
        </button>}
        {loading && <div className="text-[#666666] text-sm self-center">جارٍ التحميل...</div>}
        {groups.map((g) => (
          <button key={g.merchant_id} onClick={() => setViewer({ group: g, index: 0 })} data-testid={`story-group-${g.merchant_id}`} className="flex flex-col items-center min-w-[72px] gap-1">
            <div className={`w-16 h-16 rounded-full p-[3px] ${g.has_unseen ? "bg-gradient-to-tr from-[#09637E] via-[#088395] to-[#7AB2B2]" : "bg-gray-300"}`}>
              <div className="w-full h-full rounded-full overflow-hidden bg-white border-2 border-white">
                <img src={g.stories[0].image_url} alt="" className="w-full h-full object-cover" />
              </div>
            </div>
            <span className="text-xs font-bold text-[#333333] truncate max-w-[72px]">{g.merchant_name}</span>
          </button>
        ))}
      </div>

      {showCreate && (
        <CreateStoryModal onClose={() => setShowCreate(false)} onCreated={load} />
      )}
      {viewer && (
        <StoryViewer group={viewer.group} groups={groups} startIndex={viewer.index} onClose={() => setViewer(null)} onReloadGroups={load} />
      )}
    </div>
  );
};

export default StoriesRing;
